<template>
  <div class="dashboard p-4">
    <div class="w-full">
      <div class="grid grid-cols-1 mb-3">
        <div class="flex justify-between items-center">
        <div>
          <p class="uppercase text-xs text-gray-700 font-semibold">{{ t('reportDashboards.overview') }}</p>
          <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">
            {{ t('reportDashboards.dashboard') }}
          </h1>
          </div>
          <div class="filters-container flex items-center space-x-4" >
            <div class="flex items-center space-x-2 ml-4">
               <div class="form-control">
                 <label class="cursor-pointer label flex space-x-2 items-center">
                   <span class="label-text text-sm">{{ t('reportDashboards.autoRefresh') }}</span> 
                   <input type="checkbox" v-model="isAutoRefreshEnabled" class="toggle toggle-sm toggle-primary ml-2" />
                 </label>
               </div>
               <select v-model="autoRefreshInterval" :disabled="isAutoRefreshEnabled == false" class="select select-bordered select-sm w-[120px]">
                  <option :value="30000">30 {{ t('reportDashboards.seconds') }}</option>
                  <option :value="60000">1 {{ t('reportDashboards.minute') }}</option>
                  <option :value="300000">5 {{ t('reportDashboards.minutes') }}</option>
                  <option :value="600000">10 {{ t('reportDashboards.minutes') }}</option>
               </select>
            </div>
            <div class="form-control min-w-[200px]">
              <label class="label sr-only">
                <span class="label-text font-medium">{{ t('reportDashboards.selectAgents') }}</span>
              </label>
              <MultiSelect
                v-model="selectedAgentIds"
                :options="aiAgents"
                :placeholder="t('reportDashboards.selectAgents')"
                id-field="agentid"
                label-field="agentname"
                :is-loading="aiAgents.length == 0"
              />
            </div>

            <div class="form-control min-w-[250px]">
              <label class="label sr-only">
                <span class="label-text font-medium">{{ t('reportDashboards.dateRange') }}</span>
              </label>
              <div class="relative z-50">
                <CustomDateRangePicker
                  v-model="dateRange"
                  :clearable="false"
                  :placeholder="t('reportDashboards.selectDateRange')"
                  :auto-apply="true"
                  :multi-calendars="true"
                  class="w-full"
                />
              </div>
            </div>

            <div class="form-control min-w-[180px]">
              <label class="label sr-only">
                <span class="label-text font-medium">{{ t('reportDashboards.quickSelect') }}</span>
              </label>
              <select v-model="selectedPreset" class="select select-bordered w-full" @change="applyPreset">
                <option value="">{{ t('reportDashboards.customRange') }}</option>
                <option v-for="(label, key) in presets" :key="key" :value="key">
                  {{ t('reportDashboards.presets.' + key) }}
                </option>
              </select>
            </div>
             <button class="btn" @click="fetchDashboardData" :disabled="isLoadingData == true">
               <span v-if="isLoadingData == true" class="loading loading-spinner loading-sm mr-2"></span>
               {{ t('reportDashboards.run') }}
             </button>


          </div>
        </div>
      </div>

      <div v-if="isLoadingData === true" class="flex min-h-[24rem] items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-14 w-14 animate-spin text-indigo-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" class="opacity-25" />
          <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="opacity-90" />
        </svg>
      </div>
      <template v-else>
      <div id="topCards" class="grid grid-cols-5 mb-3">
        <div class="card mr-2 shadow bg-white dark:bg-gray-800 rounded-md p-5 border dark:border-gray-700 flex flex-col justify-between">
          <div class="flex items-center">
            <Icon icon="uim:analysis" width="20" />
            <p class="ml-2 mt-1 text-gray-900 dark:text-gray-200 text-base">
              {{ t('reportDashboards.overallCustomersSentiment') }}
            </p>
            
            <ToolTip class="absolute" :style="isRtl === true ? { left: '0.4rem', top: '0.4rem' } : { right: '0.4rem', top: '0.4rem' }">
              <h2 class="card-title">{{ t('reportDashboards.overallSentiment') }}</h2>
              <p>{{ t('reportDashboards.overallSentimentTooltip') }}</p>
            </ToolTip>
          </div>
          <h1 class="mt-4 text-bold text-right" :class="sentimentClass" style="font-size: 24px;">
            {{ overallCustomerSentimentString }}
          </h1>
        </div>
        <div class="relative">
          <KpiCard class="mr-2"
            :title="t('reportDashboards.queriesHandled')"
            :value="queriesHandled"
            icon="tabler:message-question"
          />
          
          <ToolTip class="absolute" :style="isRtl === true ? { left: '1rem', top: '0.4rem' } : { right: '1rem', top: '0.4rem' }">
            <h2 class="card-title">{{ t('reportDashboards.queriesHandled') }}</h2>
            <p>{{ t('reportDashboards.queriesHandledTooltip') }}</p>
          </ToolTip>
        </div>
        <div class="relative">
           <KpiCard class="mr-2"
            :title="t('reportDashboards.conversationsHandled')"
            :value="conversationsHandled"
            icon="tabler:message-2"
          />
          
          <ToolTip class="absolute" :style="isRtl === true ? { left: '0.8rem', top: '0.4rem' } : { right: '0.8rem', top: '0.4rem' }">
            <h2 class="card-title">{{ t('reportDashboards.conversationsHandled') }}</h2>
            <p>{{ t('reportDashboards.conversationsHandledTooltip') }}</p>
          </ToolTip>
        </div>
        <div class="relative">
           <KpiCard class="mr-2"
            :title="t('reportDashboards.avgMessagesToResolution')"
            :value="avgMessagesToResolution"
            icon="tabler:message-share"
          />
          
          <ToolTip class="absolute" :style="isRtl === true ? { left: '0.8rem', top: '0.4rem' } : { right: '0.8rem', top: '0.4rem' }">
            <h2 class="card-title">{{ t('reportDashboards.avgMessagesToResolutionShort') }}</h2>
            <p>{{ t('reportDashboards.avgMessagesToResolutionTooltip') }}</p>
          </ToolTip>
        </div>
        <div class="relative">
           <KpiCard
            :title="t('reportDashboards.chatAbandonRate')"
            :value="isChatAbandonedRate"
            icon="tabler:logout"
            unit="%"
            valueClass="text-red-500"
          />
          
          <ToolTip class="absolute" :style="isRtl === true ? { left: '0.4rem', top: '0.4rem' } : { right: '0.4rem', top: '0.4rem' }">
            <h2 class="card-title">{{ t('reportDashboards.chatAbandonRate') }}</h2>
            <p>{{ t('reportDashboards.chatAbandonRateTooltip') }}</p>
          </ToolTip>
        </div>

        <div class="card hidden mr-2 shadow bg-white dark:bg-gray-800 rounded-md p-5 border dark:border-gray-700 flex">
          <div class="flex items-center">
            <Icon icon="gg:insights" width="20" />
            <p class="ml-2 mt-1 text-gray-900 dark:text-gray-200 text-base">
              {{ t('reportDashboards.overallAgentsBehaviour') }}
            </p>
            
            <ToolTip class="absolute" :style="isRtl === true ? { left: '0.4rem', top: '0.4rem' } : { right: '0.4rem', top: '0.4rem' }">
              <h2 class="card-title">{{ t('reportDashboards.overallAgentsBehaviour') }}</h2>
              <p>{{ t('reportDashboards.overallAgentsBehaviourTooltip') }}</p>
            </ToolTip>
          </div>
          <h1 class="mt-4 text-bold text-green-300" style="font-size: 24px;">{{ t('reportDashboards.positive') }}</h1>

        </div>
        <div class="card hidden mr-2 shadow bg-white dark:bg-gray-800 rounded-md p-5 border dark:border-gray-700 flex">
          <div class="flex items-center">
            <Icon icon="tabler:shield-lock" width="20" />
            <p class="ml-2 mt-2 text-gray-900 dark:text-gray-200 text-base">
              {{ t('reportDashboards.agentsGuidanceCompliance') }}
            </p>
          </div>
          <h1 class="mt-4 text-semi-bold text-grey-300" style="font-size: 24px;">20%</h1>
        </div>
        <div class="card hidden shadow bg-white dark:bg-gray-800 rounded-md p-5 border dark:border-gray-700 flex col-span-2">
          <div class="flex items-center">
            <Icon icon="tabler:message" width="20" />
            <p class="ml-2 mt-1 text-gray-900 dark:text-gray-200 text-base">
              {{ t('reportDashboards.overallSummary') }}
            </p>
          </div>

          <h1 class="mt-1 text-bold text-grey-300" style="font-size: 18px;direction: rtl;"
            :class="textDirectionClass('היי')">
            {{ t('reportDashboards.overallSummaryText') }}
          </h1>
        </div>
      </div>

      <div class="flex" id="first-second-rows">
        <div class="grid grid-cols-12 flex-1 mr-2">
          <div class="grid grid-cols-3 col-span-12">
            <div
              class="card relative mr-2 mb-2 shadow bg-white dark:bg-gray-800 rounded-md p-5 border dark:border-gray-700 flex justify-center items-center"
              style="max-height: 250px;">
              <ToolTip class="absolute" style="right: 0.4rem; top: 0.4rem;">
                 <h2 class="card-title">{{ t('reportDashboards.netPromoterScore') }}</h2>
                 <p>{{ t('reportDashboards.npsTooltip') }}</p>
              </ToolTip>
              <div style="max-width:280px;height: -webkit-fill-available;">
                <VueUiSparkgauge :config="npsConfig" :dataset="npsDataset" />
              </div>
            </div>
            <div
              class="card relative mr-2 mb-2 shadow bg-white dark:bg-gray-800 rounded-md p-5 border dark:border-gray-700 flex items-center justify-center"
              style="height: 250px;width:-webkit-fill-available">
              <ToolTip class="absolute" style="right: 0.4rem; top: 0.4rem;">
                <h2 class="card-title">{{ t('reportDashboards.topMostUsedTags') }}</h2>
                <p>{{ t('reportDashboards.topMostUsedTagsTooltip') }}</p>
              </ToolTip>
              <VueUiSparkbar :config="topTagsConfig" :dataset="topTagsDataset" />
            </div>
            <div
              class="card relative mb-2 mr-2 shadow bg-white dark:bg-gray-800 rounded-md p-5 border dark:border-gray-700 flex items-center"
              style="max-height: 250px;max-width: 400px;">
               <ToolTip class="absolute" style="right: 0.4rem; top: 0.4rem;">
                 <h2 class="card-title">{{ t('reportDashboards.followUpConversations') }}</h2>
                 <p>{{ t('reportDashboards.followUpConversationsTooltip') }}</p>
               </ToolTip>
              <div style="height:-webkit-fill-avialable;width: 280px;">
                <VueUiSparkgauge :config="followUpConfig" :dataset="followUpDataset" />
              </div>
            </div>

          </div>
          <div class="grid grid-cols-5 col-span-12">
            <div
              class="card relative mr-2 shadow bg-white dark:bg-gray-800 rounded-md p-5 border dark:border-gray-700 flex items-center justify-center col-span-2"
              style="max-height: 490px;height: -webkit-fill-available;">
              <ToolTip class="absolute" style="right: 0.4rem; top: 0.4rem;">
                <h2 class="card-title">{{ t('reportDashboards.conversationResolutionStatus') }}</h2>
                <p>{{ t('reportDashboards.crsTooltip') }}</p>
              </ToolTip>
              <VueUiDonut :config="crsConfig" :dataset="crsDataset" />
            </div>

            <div class="card relative shadow bg-white dark:bg-gray-800 rounded-md p-5 border dark:border-gray-700 mr-2 col-span-3" style="max-height: 490px;  height: -webkit-fill-available;">
              <ToolTip class="absolute" style="right: 0.4rem; top: 0.4rem;">
                <h2 class="card-title">{{ t('reportDashboards.dailyConversations') }}</h2>
                <p>{{ t('reportDashboards.dailyConversationsTooltip') }}</p>
              </ToolTip>
              <VueUiStackbar
                  :config="dailyUsageConfig"
                  :dataset="dailyUsageDataset" style="width: 98%;"
              />
            </div>
          </div>



        </div>
        <div class="bg-white shadow dark:bg-gray-800 p-4 border dark:border-gray-700 rounded-md relative"
          style="width:28rem; max-height: 80vh; overflow:hidden">
          <div>
            <h2 class="text-lg font-semibold dark:text-gray-200">
              {{ t('reportDashboards.latestUnansweredQuestions') }}
            </h2>
            <p class="text-gray-400 text-sm">{{ t('reportDashboards.last10Unanswered') }}</p>
            <ToolTip class="absolute" style="left: 0.4rem; top: 0.4rem;" position="left">
              <h2 class="card-title">{{ t('reportDashboards.latestUnansweredQuestions') }}</h2>
              <p>{{ t('reportDashboards.latestUnansweredTooltip') }}</p>
            </ToolTip>
          </div>
          <perfect-scrollbar class="divide-y mt-3 dark:divide-gray-700 pr-2 overflow-y-auto" style="max-height: calc(80vh - 72px);">
            <div v-if="latestUnanswered.length == 0" class="text-center text-gray-500 py-4">
                {{ t('reportDashboards.noUnansweredQuestions') }}
            </div>
            <button v-else v-for="(item, index) in latestUnanswered" :key="index" 
                    @click="handleQuestionClick(item)" 
                    class="btn btn-ghost justify-start h-auto p-3 rounded-md mb-2 shadow-sm w-full text-left normal-case font-normal bg-base-200 dark:bg-gray-700 hover:bg-base-300 dark:hover:bg-gray-600">
                <div class="flex items-start space-x-2">
                    <Icon icon="tabler:help-circle" class="text-blue-500 mt-1 shrink-0" width="18" />
                    <div class="flex-1">
                        <p class="text-sm text-gray-800 dark:text-gray-200">{{ item.question }}</p>
                        <p class="text-xs text-gray-400 mt-1">{{ formatTimestamp(item.timestamp) }}</p>
                    </div>
            </div>
            </button>
          </perfect-scrollbar>
        </div>
      </div>
      </template>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue';
import { useI18n } from '@/i18n';
import { VueUiDonut, VueUiSparkgauge, VueUiSparkbar, VueUiStackbar } from "vue-data-ui";
import { Icon } from "@iconify/vue/dist/offline";
import ToolTip from "../components/Tooltip.vue";
import KpiCard from "../components/KpiCard.vue";
import CustomDateRangePicker from '@/components/CustomDateRangePicker.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useToast } from 'vue-toastification';
import router from '@/router';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import { getAllAIChats } from '@/shared/services/aiChatsService';
import { formatDateForViewer } from '@/shared/utils/dateTime';

const { t, currentLanguage } = useI18n();
const isRtl = computed(() => currentLanguage.value === 'he');
const toast = useToast();
const aiAgents = ref([]);
const selectedAgentIds = ref([]);
const dateRange = ref([]);
const selectedPreset = ref('');
const isLoadingData = ref(false);
const isAutoRefreshEnabled = ref(false);
const autoRefreshInterval = ref(60000);
const autoRefreshTimerId = ref(null);

const queriesHandled = ref(0);
const conversationsHandled = ref(0);
const avgMessagesToResolution = ref(0);
const isChatAbandonedCount = ref(0);
const isChatAbandonedRate = ref(0);
const overallCustomerSentimentString = ref('Neutral');

const latestUnanswered = ref([]);

const topTagsConfig = ref({
  theme: "",
  customPalette: [],
  style: {
    fontFamily: "inherit",
    backgroundColor: "#FFFFFF00",
    animation: {
      show: true,
      animationFrames: 60
    },
    layout: {
      independant: true,
      percentage: false,
      target: 0,
      showTargetValue: false,
      targetValueText: ""
    },
    gutter: {
      backgroundColor: "#e1e5e8ff",
      opacity: 100
    },
    bar: {
      gradient: {
        show: true,
        intensity: 40,
        underlayerColor: "#FFFFFF"
      }
    },
    labels: {
      fontSize: 14,
      name: {
        position: "top-left",
        width: "100%",
        color: "#1A1A1Aff",
        bold: false
      },
      value: {
        show: true,
        bold: true,
        prefix: '',
        suffix: '',
        rounding: 0,
        color: '#1A1A1A'
      }
    },
    title: {
      text: t('reportDashboards.topMostUsedTags'),
      color: "#1A1A1Aff",
      fontSize: 18,
      bold: false,
      textAlign: "center",
      margin: "0 0 10px 0",
      subtitle: {
        color: "#CCCCCCff",
        text: "",
        fontSize: 16,
        bold: false
      }
    },
    gap: 6
  }
});

const topTagsDataset = ref([]);

const dailyUsageConfig = ref({
  theme: '',
  responsive: true,
  customPalette: [],
  useCssAnimation: true,
  orientation: 'vertical',
  table: {
      show: false,
      responsiveBreakpoint: 400,
      columnNames: {
          period: 'Date',
          total: 'Total Conversations'
      },
      th: { backgroundColor: '#FFFFFFff', color: '#1A1A1Aff', outline: 'none' },
      td: { backgroundColor: '#FFFFFFff', color: '#1A1A1Aff', outline: 'none', roundingValue: 0 }
  },
  userOptions: { show: true, position: 'right', buttons: { tooltip: true, pdf: false, csv: true, img: false, table: true, labels: true, fullscreen: true } },
  style: {
      fontFamily: 'inherit',
      chart: {
          backgroundColor: '#FFFFFFff',
          color: '#2D353Cff',
          height: 350,
          padding: { top: 12, right: 12, bottom: 36, left: 49 },
          title: {
              text: t('reportDashboards.dailyConversations'),
              color: '#1A1A1Aff',
              fontSize: 18,
              bold: false,
              textAlign: 'center',
              subtitle: { color: '#A1A1A1ff', text: '', fontSize: 16, bold: false }
          },
          legend: { show: true, bold: false, backgroundColor: '#FFFFFFff', color: '#1A1A1Aff', fontSize: 14 },
          tooltip: { show: true, color: '#1A1A1Aff', backgroundColor: '#FFFFFFff', fontSize: 14, showPercentage: true, roundingValue: 0, roundingPercentage: 0 },
          bars: {
              gapRatio: 0.5,
              borderRadius: 0,
              strokeWidth: 1,
              gradient: { show: false },
              totalValues: { show: true, offsetY: 0, fontSize: 14, bold: false, color: '#1A1A1Aff' },
              dataLabels: { show: true, hideEmptyValues: true, color: '#1A1A1Aff', fontSize: 12, bold: false, rounding: 0 }
          },
          grid: {
              scale: { ticks: 5 },
              x: {
                  showAxis: true,
                  showHorizontalLines: false,
                  axisColor: '#E1E5E8',
                  axisThickness: 1,
                  timeLabels: { show: true, values: [], offsetY: 0, rotation: -45, fontSize: 12, color: '#1A1A1Aff' }
              },
              y: {
                  showAxis: true,
                  showVerticalLines: true,
                  axisColor: '#E1E5E8',
                  axisThickness: 1,
                  axisName: { show: false },
                  axisLabels: { show: true, color: '#1A1A1Aff', fontSize: 12, rounding: 0 }
              }
          }
      }
  }
});

const dailyUsageDataset = ref([
    {
        name: 'Resolved',
        series: [],
        color: '#5f8aee' 
    },
    {
        name: 'Not Resolved',
        series: [],
        color: '#e47f21' 
    }
]);

const presets = {
  today: 'Today',
  thisWeek: 'This Week',
  lastWeek: 'Last Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month'
};

const presetRanges = {
  today: () => {
    const today = new Date();
    return [today, today];
  },
  thisWeek: () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    return [startOfWeek, today];
  },
  lastWeek: () => {
    const today = new Date();
    const startOfLastWeek = new Date(today);
    startOfLastWeek.setDate(today.getDate() - today.getDay() - 7);
    const endOfLastWeek = new Date(today);
    endOfLastWeek.setDate(today.getDate() - today.getDay() - 1);
    return [startOfLastWeek, endOfLastWeek];
  },
  thisMonth: () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return [startOfMonth, today];
  },
  lastMonth: () => {
    const today = new Date();
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    return [startOfLastMonth, endOfLastMonth];
  }
};

 

const sentimentClass = computed(() => {
  if (overallCustomerSentimentString.value !== undefined) {
    const sentiment = overallCustomerSentimentString.value.toLowerCase();
    if (sentiment.includes('positive')==true) return 'text-green-500';
    if (sentiment.includes('negative')==true) return 'text-red-500';
    if (sentiment.includes('mixed')==true) return 'text-yellow-500';
  }
  return 'text-gray-500'; 
});

onMounted(async () => {
  loadStyle(`
html[dir="rtl"] .ml-2 {
  margin-right: 0.5rem;
}
html[dir="rtl"] .ml-4 {
  margin-right: 1rem;
}
html[dir="rtl"] .mr-2 {
  margin-left: 0.5rem;
}
html[dir="rtl"] .ml-auto {
  margin-left: initial;
  margin-right: auto;
}
#footer {
  display: none;
}
[title="Open options"],[title="Close options"]{
  top: -36px !important;
  right: -8px !important;
  display: none !important;
}
.vue-ui-user-options-drawer{
right: -8px !important;
top: 0px !important;
}

.dp__main input {
  width:100% !important;
  text-align:center;
  padding-right:3rem;
}
  .dp__outer_menu_wrap {
    z-index: 9999 !important;
  }
  `);

  await loadAgents();

  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 7); 
  const endDate = new Date();
  dateRange.value = [startDate, endDate];

  await fetchDashboardData(); 

});

const getAuthToken = () => {
  const row = document.cookie.split('; ').find(row => row.startsWith('authToken=')!=false);
  const token = isDefinedAndNotEmpty(row) != false ? row.split('=')[1] : '';
  return token !== undefined && token !== null ? token : '';
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '') {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const handleAuthError = (error) => {
  if (isDefinedAndNotEmpty(error.response) != false && (error.response.status === 401 || error.response.status === 403)) { 
    router.push('/login');
  }
};

const loadAgents = async () => {
  try {
    const data = await getAllAIChats({ includeStyles: false });
    aiAgents.value = Array.isArray(data) === true ? data : [];
  } catch (error) {
    toast.error('Failed to load AI agents');
    console.error('Error loading agents:', error);
    handleAuthError(error);
  }
};

 

const applyPreset = () => {
  if (isDefinedAndNotEmpty(selectedPreset.value) != false && isDefinedAndNotEmpty(presetRanges[selectedPreset.value]) != false) {
    dateRange.value = presetRanges[selectedPreset.value]();
  }
};

const formatDateHardcoded = (dateValue) => {
  return formatDateForViewer(dateValue);
};

const formatRangeForPicker = (dates) => {
  if (!isDefinedAndNotEmpty(dates)) {
    return '';
  }
  let startDate, endDate;
  
  if (Array.isArray(dates) != false && dates.length === 2 && Array.isArray(dates[0]) == false) {
    [startDate, endDate] = dates;
  
  } else if (Array.isArray(dates) != false && Array.isArray(dates[0]) != false && dates[0].length === 2) {
    [startDate, endDate] = dates[0];
  } else {
    return '';
  }
  const start = formatDateHardcoded(startDate);
  const end = formatDateHardcoded(endDate);
  const startDateOnly = start.split(',')[0];
  const endDateOnly = end.split(',')[0];
  return `${startDateOnly} - ${endDateOnly}`;
};

function loadStyle(cssString) {
  const style = document.createElement('style');
  style.type = 'text/css';

  if (style.styleSheet !== undefined) {
    style.styleSheet.cssText = cssString;
  } else {
    style.appendChild(document.createTextNode(cssString));
  }

  document.head.appendChild(style);
}

const crsConfig = ref({ "type": "classic", "responsive": false, "theme": "", "customPalette": [], "useCssAnimation": true, "useBlurOnHover": true, "userOptions": { "show": true, "position": "right", "buttons": { "tooltip": true, "pdf": false, "csv": true, "img": false, "table": true, "labels": true, "fullscreen": true, "sort": false, "stack": false, "animation": false, "annotator": true }, "buttonTitles": { "open": "Open options", "close": "Close options", "tooltip": "Toggle tooltip", "pdf": "Download PDF", "csv": "Download CSV", "img": "Download PNG", "table": "Toggle table", "labels": "Toggle labels", "fullscreen": "Toggle fullscreen", "annotator": "Toggle annotator" } }, "translations": { "total": "Total", "average": "Average" }, "table": { "show": false, "responsiveBreakpoint": 400, "th": { "backgroundColor": "#FFFFFFff", "color": "#1A1A1Aff", "outline": "none" }, "td": { "backgroundColor": "#FFFFFFff", "color": "#1A1A1Aff", "outline": "none", "roundingValue": 0, "roundingPercentage": 0 }, "columnNames": { "series": "Series", "value": "Value", "percentage": "Percentage" } }, "style": { "fontFamily": "inherit", "chart": { "useGradient": false, "gradientIntensity": "49", "backgroundColor": "#FFFFFFff", "color": "#1A1A1Aff", "layout": { "labels": { "dataLabels": { "show": true, "useLabelSlots": false, "hideUnderValue": 3, "prefix": "", "suffix": "" }, "value": { "rounding": 0, "show": false, "formatter": null }, "percentage": { "color": "#1A1A1Aff", "bold": true, "fontSize": 18, "rounding": 0, "formatter": null }, "name": { "color": "#1A1A1Aff", "bold": false, "fontSize": 16 }, "hollow": { "show": true, "total": { "show": true, "bold": false, "fontSize": 18, "color": "#AAAAAAff", "text": "Total", "offsetY": 0, "value": { "color": "#1A1A1Aff", "fontSize": 18, "bold": true, "suffix": "", "prefix": "", "offsetY": 0, "rounding": 0, "formatter": null } }, "average": { "show": true, "bold": false, "fontSize": 18, "color": "#AAAAAAff", "text": "Average", "offsetY": 0, "value": { "color": "#1A1A1Aff", "fontSize": 18, "bold": true, "suffix": "", "prefix": "", "offsetY": 0, "rounding": 0, "formatter": null } } } }, "donut": { "strokeWidth": 55, "borderWidth": 1, "useShadow": false, "shadowColor": "#1A1A1A" } }, "comments": { "show": true, "showInTooltip": true, "width": 100, "offsetY": 0, "offsetX": 0 }, "legend": { "show": true, "bold": false, "backgroundColor": "#FFFFFFff", "color": "#1A1A1Aff", "fontSize": 16, "roundingValue": 0, "roundingPercentage": 0 }, "tooltip": { "show": true, "color": "#1A1A1Aff", "backgroundColor": "#FFFFFFff", "fontSize": 14, "customFormat": null, "borderRadius": 4, "borderColor": "#e1e5e8", "borderWidth": 1, "backgroundOpacity": 30, "position": "center", "offsetY": 24, "showValue": true, "showPercentage": true, "roundingValue": 0, "roundingPercentage": 0 }, "title": { "text": t('reportDashboards.conversationResolutionStatus'), "color": "#1A1A1Aff", "fontSize": 18, "bold": false, "textAlign": "center", "paddingLeft": 0, "paddingRight": 0, "subtitle": { "color": "#A1A1A1ff", "text": "", "fontSize": 16, "bold": false } } } } })

const crsOverMonthsConfig = ref({
  "userOptions": {
    "show": false,
    "position": "right",
    "buttons": {
      "tooltip": true,
      "pdf": true,
      "csv": true,
      "img": true,
      "table": true,
      "labels": true,
      "fullscreen": true,
      "sort": false,
      "stack": false,
      "animation": false,
      "annotator": true
    }
  },
  "style": {
    "fontFamily": "inherit",
    "chart": {
      "backgroundColor": "#FFFFFFff",
      "color": "#2D353Cff",
      "height": 500,
      "width": 800,
      "padding": {
        "top": 12,
        "right": 12,
        "bottom": 36,
        "left": 48
      },
      "title": {
        "text": t('reportDashboards.conversationResolutionStatus'),
        "color": "#1A1A1Aff",
        "fontSize": 17,
        "bold": true,
        "textAlign": "center",
        "paddingLeft": 0,
        "paddingRight": 0,
        "subtitle": {
          "color": "#514d4dff",
          "text": "Over months",
          "fontSize": 18,
          "bold": false
        }
      }
    }
  }
});


const crsOverMonthsDataset = ref([
  {
    "name": "Solved",
    "series": [
      10,
      20,
      30
    ],
    "color": "#5f8aee"
  },
  {
    "name": "Didn't Solve",
    "series": [
      20,
      40,
      60
    ],
    "color": "#42d392"
  }
]);

const followUpConfig = ref({
  "theme": "",
  "style": {
    "fontFamily": "inherit",
    "background": "#FFFFFF",
    "height": 14,
    "width": 20,
    "basePosition": 72,
    "animation": {
      "show": true,
      "speedMs": 150
    },
    "title": {
      "show": true,
      "text": t('reportDashboards.followUpConversations'),
      "fontSize": 18,
      "position": "top",
      "textAlign": "center",
      "bold": false,
      "color": "#1A1A1Aff"
    },
    "dataLabel": {
      "fontSize": 20,
      "autoColor": true,
      "color": "#1A1A1Aff",
      "offsetY": 0,
      "bold": true,
      "rounding": 0,
      "prefix": "",
      "suffix": "%",
      "formatter": null
    },
    "colors": {
      "min": "#FF0000",
      "max": "rgba(0, 255, 0, 1)",
      "showGradient": false
    },
    "track": {
      "autoColor": false,
      "color": "#5f8bee99",
      "strokeLinecap": "round"
    },
    "gutter": {
      "color": "#e1e5e8ff",
      "strokeLinecap": "round"
    }
  }
});


const npsConfig = ref({
  "theme": "",
  "style": {
    "fontFamily": "inherit",
    "background": "#FFFFFF",
    "height": 14,
    "width": 20,
    "basePosition": 72,
    "animation": {
      "show": true,
      "speedMs": 150
    },
    "title": {
      "show": true,
      "text": t('reportDashboards.netPromoterScore'),
      "fontSize": 18,
      "position": "top",
      "textAlign": "center",
      "bold": false,
      "color": "#1A1A1Aff"
    },
    "dataLabel": {
      "fontSize": 20,
      "autoColor": true,
      "color": "#1A1A1Aff",
      "offsetY": 0,
      "bold": true,
      "rounding": 0,
      "prefix": "",
      "suffix": "%",
      "formatter": null
    },
    "colors": {
      "min": "#FF0000",
      "max": "rgba(0, 255, 0, 1)",
      "showGradient": false
    },
    "track": {
      "autoColor": false,
      "color": "#42d392ff",
      "strokeLinecap": "round"
    },
    "gutter": {
      "color": "#e1e5e8ff",
      "strokeLinecap": "round"
    }
  }
});


const npsDataset = ref({
  "value": 0,
  "min": 0,
  "max": 100,
  "title": t('reportDashboards.netPromoterScore')
});
const followUpDataset = ref({
  "value": 0,
  "min": 0,
  "max": 100,
  "title": t('reportDashboards.followUpConversations')
});

const topUsedTopicsDataset = ref([]);

const crsDataset = ref([{ "name": "Solved", "values": [0], "color": "#1f77b4" }, { "name": "Didn't Solve", "values": [0], "color": "#e47f21" }]);

const startAutoRefresh = () => {
  stopAutoRefresh(); 
  if (isAutoRefreshEnabled.value === true && autoRefreshInterval.value > 0) {
    autoRefreshTimerId.value = setInterval(fetchDashboardData, autoRefreshInterval.value);
  }
};

const stopAutoRefresh = () => {
  if (autoRefreshTimerId.value !== null) {
    clearInterval(autoRefreshTimerId.value);
    autoRefreshTimerId.value = null;
  }
};

watch(isAutoRefreshEnabled, (newValue) => {
  if (newValue === true) {
    startAutoRefresh();
  } else {
    stopAutoRefresh();
  }
});

watch(autoRefreshInterval, () => {
  if (isAutoRefreshEnabled.value !== false) {
    startAutoRefresh(); 
  }
});

const fetchDashboardData = async () => {
  stopAutoRefresh(); 
  isLoadingData.value = true;
  try {
    const params = {
      agentIds: selectedAgentIds.value,
      startDate: dateRange.value[0] !== undefined ? dateRange.value[0].toISOString() : null,
      endDate: dateRange.value[1] !== undefined ? dateRange.value[1].toISOString() : null,
    };

    const response = await axios.get(`${globalState.apiEndpoint}/api/reports/conversationsAnalysis`, {
      headers: getAuthHeaders(),
      params: params
    });

    const data = response.data;
    console.log("Dashboard data fetched:", data);

    if (data === undefined || data === null) {
      toast.error("Received invalid data from server.");
      return;
    }

    if (data.averageNps !== undefined && data.averageNps !== null) {
      npsDataset.value.value = Math.round(data.averageNps);
    } else {
      npsDataset.value.value = 0;
    }

    if (data.followUps !== undefined && data.followUps !== null && (data.followUps.true + data.followUps.false) > 0) {
      followUpDataset.value.value = Math.round((data.followUps.true / (data.followUps.true + data.followUps.false)) * 100);
    } else {
      followUpDataset.value.value = 0;
    }

    let solvedCount = 0;
    let notSolvedCount = 0;
    if (isDefinedAndNotEmpty(data.resolutions) != false) {
        solvedCount = parseInt(isDefinedAndNotEmpty(data.resolutions.solved) != false ? data.resolutions.solved : 0, 10);
        notSolvedCount = parseInt(isDefinedAndNotEmpty(data.resolutions.notSolved) != false ? data.resolutions.notSolved : 0, 10);
    }
    crsDataset.value = [
        { name: "Solved", values: [solvedCount], color: "#1f77b4" }, 
        { name: "Didn't Solve", values: [notSolvedCount], color: "#e47f21" }
    ];

    queriesHandled.value = isDefinedAndNotEmpty(data.totalQueries) != false ? data.totalQueries : 0;
    conversationsHandled.value = isDefinedAndNotEmpty(data.totalSessions) != false ? data.totalSessions : 0;
    avgMessagesToResolution.value = isDefinedAndNotEmpty(data.avgMessagesToResolution) != false ? data.avgMessagesToResolution : 0;
    isChatAbandonedRate.value = isDefinedAndNotEmpty(data.isChatAbandonedRate) != false ? data.isChatAbandonedRate : 0;
    overallCustomerSentimentString.value = isDefinedAndNotEmpty(data.overallSentiment) != false ? data.overallSentiment : 'Neutral';
    latestUnanswered.value = isDefinedAndNotEmpty(data.latestUnansweredQuestions) != false ? data.latestUnansweredQuestions : [];

    if (data.dailyBreakdown !== undefined && Array.isArray(data.dailyBreakdown.dates) === true) {
      dailyUsageConfig.value.style.chart.grid.x.timeLabels.values = data.dailyBreakdown.dates.map(d => {
        try {
          const organizationTimezone = isDefinedAndNotEmpty(globalState.organizationTimezone) != false ? globalState.organizationTimezone : 'UTC'; 
          const dateObj = new Date(d);
          if (organizationTimezone === 'Asia/Jerusalem') {
              return `${String(dateObj.getDate()).padStart(2, '0')}/${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
          } else {
              return `${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
          }
        } catch (e) {
          console.error("Error formatting date label:", e, d);
          return d; 
        }
      });

      dailyUsageDataset.value = [
          {
              name: 'Resolved',
              series: isDefinedAndNotEmpty(data.dailyBreakdown.resolved) != false ? data.dailyBreakdown.resolved : [],
              color: '#5f8aee' 
          },
          {
              name: 'Not Resolved',
              series: isDefinedAndNotEmpty(data.dailyBreakdown.unresolved) != false ? data.dailyBreakdown.unresolved : [],  
              color: '#e47f21' 
          }
      ];
    } else {
      dailyUsageConfig.value.style.chart.grid.x.timeLabels.values = [];
      dailyUsageDataset.value = [
          { name: 'Resolved', series: [], color: '#5f8aee' },
          { name: 'Not Resolved', series: [], color: '#e47f21' }
      ];
    }


    if (Array.isArray(data.topTags) === true) {
        topTagsDataset.value = data.topTags.map((tag) => ({
            name: tag.name,
            value: tag.count,
            prefix: '',
            suffix: ''
        }));
    } else {
        topTagsDataset.value = [];
    }

    console.log('Organization Timezone:', globalState.organizationTimezone);

  } catch (error) {
    toast.error('Failed to fetch dashboard data');
    console.error('Error fetching dashboard data:', error);
    handleAuthError(error);

     npsDataset.value.value = 0;
     followUpDataset.value.value = 0;
     crsDataset.value = [
         { name: "Solved", values: [0], color: "#1f77b4" }, 
         { name: "Didn't Solve", values: [0], color: "#e47f21" }
     ];
    queriesHandled.value = 0;
    conversationsHandled.value = 0;
    avgMessagesToResolution.value = 0;
    isChatAbandonedRate.value = 0;
    overallCustomerSentimentString.value = 'Error'; 
    latestUnanswered.value = [];
    topTagsDataset.value = [];
  } finally {
    isLoadingData.value = false;
    startAutoRefresh(); 
  }
};

const formatTimestamp = (timestamp) => {
    if (timestamp === undefined || timestamp === null) return 'N/A';
    try {
        const date = new Date(timestamp);
        return date.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) {
        console.error("Error formatting timestamp:", e);
        return String(timestamp);
    }
};

const handleQuestionClick = (item) => {
  console.log("Clicked unanswered question:", item);
};  

onUnmounted(() => {
  stopAutoRefresh();
});


function detectTextDirection(text) {
  if (text === null || text === undefined || text.length < 1) return 'ltr';
  const rtlChars = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
  return rtlChars.test(text) === true ? 'rtl' : 'ltr';
}

function textDirectionClass(text) {
  return detectTextDirection(text) === 'rtl' ? 'text-rtl heebo-text' : 'text-ltr';
}

</script>
